import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PreviewRecord } from "../utils/importPreview";
import { formatCompactAmount } from "../../api/DataTypeUtils";
import { renderConnectionStrength } from "../../grid/CellRenderer";

interface PreviewTableProps {
  records: PreviewRecord[];
  entityType: "CONTACT" | "COMPANY" | "SALE";
  mappings?: Record<string, any[]>;
}

const PreviewTable: React.FC<PreviewTableProps> = ({ records, entityType, mappings }) => {
  const { t, i18n } = useTranslation();
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const projectCurrency =
    useSelector((state: any) => state.projectReducer.getIn(["project", "currency"])) || "USD";

  if (records.length === 0) {
    return <div className="text-center py-12 text-gray-500">{t("No records to preview")}</div>;
  }

  // Get all unique field IDs from records
  const allFieldIds = new Set<string>();
  records.forEach((record) => {
    Object.keys(record.data).forEach((fieldId) => allFieldIds.add(fieldId));
  });
  const fieldIds = Array.from(allFieldIds);

  // Get field info (type, label) from mappings or Redux fields
  const getFieldInfo = (fieldId: string): { label: string; type?: string } => {
    // First try from mappings (most accurate for current import)
    if (mappings && mappings[entityType]) {
      const mapping = mappings[entityType].find((m: any) => m?.destination?.value === fieldId);
      if (mapping?.destination?.label) {
        return { label: mapping.destination.label, type: mapping?.type };
      }
    }
    // Fallback to Redux fields
    if (fields) {
      const field = fields.find((f: any) => f.get("id") === fieldId);
      if (field) {
        return { label: field.get("label") || fieldId, type: field.get("type") };
      }
    }
    // Final fallback
    return { label: fieldId };
  };

  // Format value based on field type (like CellRenderer.tsx)
  const formatValue = (value: any, fieldId: string): string | React.ReactNode => {
    if (value === null || value === undefined || value === "") return "";

    const strValue = String(value);
    const numValue = parseFloat(strValue);
    if (isNaN(numValue)) return strValue;

    // Connection strength field - render as colored badge
    if (fieldId === "connectionStrength") {
      return renderConnectionStrength(numValue);
    }

    const { type: fieldType } = getFieldInfo(fieldId);

    // Currency fields (estimatedRevenue, price, amount, etc.)
    const currencyFields = ["estimatedRevenue", "price", "amount", "revenue"];
    if (fieldType === "currency" || currencyFields.some((f) => fieldId.toLowerCase().includes(f))) {
      return formatCompactAmount(i18n.language, numValue, projectCurrency, strValue);
    }

    // Integer fields - format with thousand separators, no decimals
    if (fieldType === "integer") {
      return Math.round(numValue).toLocaleString();
    }

    // Double/number fields - format with thousand separators
    if (fieldType === "double" || fieldType === "number") {
      return numValue % 1 === 0
        ? Math.round(numValue).toLocaleString()
        : numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    // Default: if it looks like a number, format it
    if (strValue.match(/^-?\d+(\.\d+)?$/)) {
      return numValue % 1 === 0
        ? Math.round(numValue).toLocaleString()
        : numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return strValue;
  };

  const getFieldLabel = (fieldId: string): string => getFieldInfo(fieldId).label;

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {fieldIds.map((fieldId) => (
              <th
                key={fieldId}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {getFieldLabel(fieldId)}
              </th>
            ))}
            {records.some((r) => r.validationErrors && r.validationErrors.length > 0) && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("Errors")}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {records.map((record, index) => (
            <tr key={index} className="hover:bg-gray-50">
              {fieldIds.map((fieldId) => (
                <td key={fieldId} className="px-4 py-3 text-sm text-gray-900">
                  {record.data[fieldId] ? (
                    formatValue(record.data[fieldId], fieldId)
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              ))}
              {records.some((r) => r.validationErrors && r.validationErrors.length > 0) && (
                <td className="px-4 py-3 text-sm">
                  {record.validationErrors && record.validationErrors.length > 0 ? (
                    <div className="space-y-1">
                      {record.validationErrors.map((error, errIndex) => (
                        <div key={errIndex} className="text-red-600 text-xs">
                          {error.message}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PreviewTable;
